import { Icon, Menu, MenuItem } from "@mui/material";
import MDTypography from "components/MDTypography";
import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ProjectMap } from "generated/graphql";
import useDeleteProjectMap from "hooks/projectMaps/useDeleteProjectMap";
import useConfirmationDialog from "hooks/useConfirmationDialog";
import useModalState from "hooks/useModalState";
import CreateProposalFromMapDialog from "./CreateProposalFromMapDialog";
import ConfirmDeleteMapDialog from "./ConfirmDeleteMapDialog";

export default function ProjectMapActionsCell({ row }: { row: { original: ProjectMap } }) {
  const navigate = useNavigate();
  const [anchorElement, setAnchorElement] = useState<Element | null>(null);
  const proposalDialogState = useModalState();
  const [deleteProjectMap, { isSuccess }] = useDeleteProjectMap();

  const acceptFn = useCallback(async () => {
    const { data } = await deleteProjectMap({ variables: { id: row.original.id } });
    return isSuccess(data);
  }, [deleteProjectMap, isSuccess, row.original.id]);

  const cancelFn = useCallback(async () => true, []);

  const {
    open: deleteDialogOpen,
    setOpen: setDeleteDialogOpen,
    onAccept,
    onCancel,
  } = useConfirmationDialog({
    acceptFn,
    cancelFn,
  });

  const closeMenu = useCallback(() => {
    setAnchorElement(null);
  }, []);

  const handleEdit = useCallback(() => {
    closeMenu();
    navigate(`/map/${row.original.id}`);
  }, [closeMenu, navigate, row.original.id]);

  const handleDeleteClick = useCallback(() => {
    closeMenu();
    setDeleteDialogOpen(true);
  }, [closeMenu, setDeleteDialogOpen]);

  const handleCreateProposal = useCallback(() => {
    closeMenu();
    proposalDialogState.onOpen();
  }, [closeMenu, proposalDialogState]);

  return (
    <>
      <Menu
        anchorEl={anchorElement}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        open={Boolean(anchorElement)}
        onClose={closeMenu}
        keepMounted
      >
        <MenuItem onClick={handleEdit}>Edit</MenuItem>
        <MenuItem onClick={handleCreateProposal}>Create proposal from map</MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: "error.main" }}>
          Delete
        </MenuItem>
      </Menu>
      <MDTypography
        color="secondary"
        display="flex"
        alignItems="center"
        justifyContent="center"
        width="100%"
        onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) =>
          setAnchorElement(e.currentTarget)
        }
      >
        <Icon sx={{ cursor: "pointer", fontWeight: "bold" }}>more_vert</Icon>
      </MDTypography>

      <ConfirmDeleteMapDialog
        open={deleteDialogOpen}
        setOpen={setDeleteDialogOpen}
        onAccept={onAccept}
        onCancel={onCancel}
        mapName={row.original.name}
      />

      <CreateProposalFromMapDialog
        open={proposalDialogState.open}
        onClose={proposalDialogState.onClose}
        projectMap={row.original}
      />
    </>
  );
}
